/**
 * Perx Overwatch palette, read off Figma frames 5726:6452 (Business Overview) and 5678:549
 * (System & Support), features/PLA-9674/design/spec.md section 6.
 *
 * The screens are drawn on their own dark set - page #0A0F1C, card #101830, inset #0D1428 - which
 * only partly overlaps the Intelligence tokens, so it is kept as a separate palette the same way
 * Growth Planner keeps its own (containers/GrowthPlanner/theme/tokens.ts). Values are literals, so
 * an antd overlay that portals out of the content scope still paints correctly. PLA-9674.
 */
import { INTELLIGENCE_FONT_FAMILY, scaledPx } from 'containers/Intelligence/theme';
import { TYPE_LINE_HEIGHT, TYPE_SCALE, TypeStep } from 'containers/Intelligence/theme/tokens';

export const ow = {
  // Surfaces
  page: '#0A0F1C',
  card: '#101830',
  inset: '#0D1428',
  panel: '#1A2237',
  tooltip: '#1F1A5F',
  track: '#E2E7F0',

  // Borders
  border: '#212D4B',
  borderInfo: '#2A344D',

  // Text
  textPrimary: '#F5F7FB',
  textSecondary: '#AEBBD0',
  textMuted: '#64709A',
  textIcon: '#98A5BF',
  textField: '#E8EDF8',
  textTile: '#FCFCFC',
  white: '#FFFFFF',

  // Accents and status tones
  accent: '#4F46E5',
  action: '#6D5DFC',
  success: '#1EB980',
  danger: '#EE6475',
  warning: '#F5A623',
  info: '#268AFF',
  infoBar: '#3D8BFF',
  cyan: '#1ED6FF',
  teal: '#20B8CD',
  pink: '#EC4899',
  mint: '#36F097',
  indigoSoft: '#6976EB'
} as const;

/* Roboto carries no diagonal arrows, and the shared stack reaches the colour emoji faces before any
   symbol face, which paints them as emoji tiles. The symbol faces go first. */
const SYMBOL_FACES = "'Segoe UI Symbol', 'Apple Symbols', 'Noto Sans Symbols 2'";

export const owFont = {
  body: INTELLIGENCE_FONT_FAMILY.replace(/^('[^']+'),/, `$1, ${SYMBOL_FACES},`),
  mono: "'IBM Plex Mono', SFMono-Regular, Menlo, Consolas, monospace"
} as const;

interface IOwType {
  size: string;
  line: string;
  /** The step in px, for the chart props that take a number rather than CSS. */
  px: number;
}

const step = (name: TypeStep): IOwType => ({
  size: scaledPx(TYPE_SCALE[name]),
  line: scaledPx(TYPE_SCALE[name] * TYPE_LINE_HEIGHT),
  px: TYPE_SCALE[name]
});

/**
 * The AI pages' nine-step type scale (PLA-8844), read from the Intelligence `TYPE_SCALE` rather than
 * its `--is-type-*` vars: the tooltips and the ticket modal portal out of the content scope, where
 * those vars do not resolve. The emitted value is the same `scaledPx` the scope declares.
 */
export const owType = {
  ...(Object.keys(TYPE_SCALE) as TypeStep[]).reduce(
    (acc, name) => ({ ...acc, [name]: step(name) }),
    {} as Record<TypeStep, IOwType>
  ),
  pageTitle: step('heading1'),
  pageSubtitle: step('body1'),
  sectionTitle: step('heading2'),
  sectionSubtitle: step('body3'),
  cardTitle: step('heading4'),
  containerTitle: step('body2'),
  containerBody: step('body3'),
  metricLabel: step('body3'),
  metric: step('heading4'),
  delta: step('body3')
};

/** A drawn px length that follows `--is-scale` when the responsive flag is on. */
export const px = scaledPx;

/** `#RRGGBB` at the given alpha, for the 14% tone tints the screens use on pills and rows. */
export const tint = (hex: string, alpha: number): string => {
  const value = parseInt(hex.slice(1), 16);

  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
};
